/**
 * Client-side base photos API module — replaces base-photos.actions.ts.
 *
 * Note: The original file was already mostly client-side (no "use server" directive,
 * used window detection). This version uses authFetch for consistent auth handling.
 */

import { authFetch, authFetchJson } from "@/shared/lib/auth-fetch"
import { ApiResponse } from "@/shared/types/backend-response.type"
import { BasePhoto } from "@/features/base/types/base-content"

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const BASE_PHOTO_QUERY_KEYS = {
  all: ["base-photos"] as const,
  byRegion: (regionId: number) => ["base-photos", "region", regionId] as const,
}

// ---------------------------------------------------------------------------
// Read
// ---------------------------------------------------------------------------

export async function getBasePhotosByRegionId(
  regionId: number,
  filters?: { tags?: string[]; limit?: number; offset?: number },
): Promise<ApiResponse<BasePhoto[]>> {
  const params = new URLSearchParams()
  if (filters?.tags?.length) params.set("tags", filters.tags.join(","))
  if (filters?.limit !== undefined) params.set("limit", filters.limit.toString())
  if (filters?.offset !== undefined) params.set("offset", filters.offset.toString())
  const query = params.toString()

  // Call the backend directly via authFetch instead of through the Next.js API route
  const url = `/base-photos/region/${regionId}${query ? `?${query}` : ""}`
  return authFetchJson<BasePhoto[]>(url)
}

// ---------------------------------------------------------------------------
// Update / Delete
// ---------------------------------------------------------------------------

export async function deleteBasePhoto(photoId: number): Promise<ApiResponse<string>> {
  return authFetchJson<string>(`/base-photos/${photoId}/permanent`, { method: "DELETE" })
}

export async function updateBasePhoto(
  photoId: number,
  updateData: { description?: string | null; takenAt?: string | null; tags?: string[] | null },
): Promise<ApiResponse<BasePhoto>> {
  const body: Record<string, unknown> = {}
  if (updateData.description !== undefined) body.description = updateData.description
  if (updateData.takenAt !== undefined) body.takenAt = updateData.takenAt
  if (updateData.tags !== undefined) body.tags = updateData.tags

  return authFetchJson<BasePhoto>(`/base-photos/${photoId}`, {
    method: "PUT",
    body: JSON.stringify(body),
  })
}
